import { useUserStore } from '@/store/modules/user/store.js';
import ActivixDate from '@/value-objects/ActivixDate.js';

import Lead from '@/entities/Lead.js';
import Model from '@/entities/Model.js';
import Reminder from '@/entities/Reminder.js';
import TaskEventType from '@/entities/TaskEventType.js';
import TaskEventTypeIcon from '@/entities/TaskEventTypeIcon.js';
import User from '@/entities/User.js';

export default class TaskEvent extends Model {
    static get defaults() {
        return {
            address: null,
            automation_action: {},
            canceled: false,
            completed_at: null,
            confirmed: false,
            customer_virtual_event_link: null,
            description: '',
            end_at: null,
            guests: [],
            lead: {},
            location: null,
            main_event: false,
            no_show: false,
            priority: 'normal',
            reminders: [],
            start_at: null,
            status: false,
            task_event_type_id: null,
            title: '',
            user: {},
            user_id: null,
            user_virtual_event_link: null,
            confirmation_timeframe: null,
            send_ics: false,
            wanted_vehicle_ids: null,
        };
    }

    constructor(initialValues = {}) {
        super(initialValues);
    }

    /**
     * Getters
     */

    get lead() {
        return this._lead;
    }

    get user() {
        if (!this.user_id) {
            return new User();
        }

        return useUserStore().getUserById(this.user_id);
    }

    get guests() {
        return this._guests
            .map((guest) => useUserStore().getUserById(guest.id))
            .filter((user) => user !== null)
            .sort((a, b) => a.fullName.localeCompare(b.fullName));
    }

    get guestIds() {
        return this._guests.map((guest) => guest.id);
    }

    get reminders() {
        return this._reminders;
    }

    get startAt() {
        return new ActivixDate(this.start_at);
    }

    /**
     * Setters
     */

    set lead(lead) {
        this._lead = new Lead(lead);
    }

    set user(user) {
        // This setter is required to handle API responses that include 'user' objects,
        // but we should not set the user object directly as it is in user store
    }

    set guests(guests) {
        this._guests = guests;
    }

    set reminders(reminders) {
        this._reminders = reminders.map((reminder) => new Reminder(reminder));
    }

    set creator(creator) {
        // This setter is required to handle API responses that include 'creator' objects,
        // but we should not set the user object directly as it is in user store
    }

    /**
     * Specifications
     */

    get hasStatus() {
        return this.canceled || this.no_show || this.status;
    }

    get icon() {
        const taskEventType = TaskEventType.getKey(this.task_event_type_id);

        return TaskEventTypeIcon.entries[taskEventType];
    }

    get iconName() {
        return TaskEventType.getIconName(this.task_event_type_id);
    }

    get isLate() {
        if (this.startAt.isFuture()) {
            return false;
        }

        return !this.hasStatus;
    }

    get isPlanned() {
        return !this.hasStatus;
    }

    get userId() {
        return this.user_id;
    }

    set userId(userId) {
        this.user_id = userId;
    }

    get creatorId() {
        return this.created_by;
    }

    set creatorId(userId) {
        this.created_by = userId;
    }

    get creator() {
        const userData = {
            first_name: 'Activix',
            last_name: 'CRM',
        };

        if (!this.creatorId) {
            return new User(userData);
        }

        return useUserStore().getUserById(this.creatorId) || new User(userData);
    }

    hasGuest(userId) {
        return this.guestIds.includes(userId);
    }

    get isTodayOrFutureVirtualAppointment() {
        return (
            this.task_event_type_id === TaskEventType.VIRTUAL_APPOINTMENT &&
            new ActivixDate(this.start_at).isSameOrAfter(new ActivixDate('now').startOfDay())
        );
    }
}
